/** A notice's box on screen (screen pixels). */
export interface NoticeRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Where a notice goes, and whether it may show now. */
export interface NoticePlace {
  /** Its top edge (screen pixels). */
  y: number;
  shown: boolean;
}

export interface NoticeHandle {
  /** Its box changed height: the ones under it move. */
  setHeight(height: number): void;
  release(): void;
}

/** Room between two notices. */
export const NOTICE_GAP = 8;

/**
 * The tops of notices `heights` tall, stacked down from `top` in the order
 * they came, `gap` apart.
 */
export function noticeLayout(top: number, heights: readonly number[], gap = NOTICE_GAP): number[] {
  const ys: number[] = [];
  let y = top;
  for (const h of heights) {
    ys.push(Math.round(y));
    y += Math.max(0, h) + gap;
  }
  return ys;
}

/** The top of the column: under the top bar on a phone, between its
 *  corners on a wide screen. */
export function noticeTop(viewWidth: number, safeTop: number): number {
  return safeTop + (viewWidth >= 700 ? 8 : 64);
}

/** The notch or status bar at the top (CSS pixels); 0 off a browser. */
function safeAreaInsetTop(): number {
  if (typeof document === 'undefined') return 0;
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:env(safe-area-inset-top,0px);visibility:hidden;pointer-events:none';
  document.body.appendChild(probe);
  const inset = probe.getBoundingClientRect().height;
  document.body.removeChild(probe);
  return inset;
}

interface Entry {
  id: number;
  height: number;
  place: (place: NoticePlace) => void;
  rect: (() => NoticeRect | null) | null;
}

declare global {
  interface Window {
    /** The notices on screen and their boxes, for browser tests. */
    __notices?: { rects: () => NoticeRect[] };
  }
}

/**
 * One column for the game's notices, wherever they're drawn (the slot's
 * canvas or the overlay canvas): "Tap SPIN to play", the gem toast, the
 * connection banner. Each new one goes under those already showing, a
 * small gap apart, and the rest move up when one goes, so no two ever
 * overlap. A full-screen moment (the refill) blocks the column: every
 * notice hides until it's over, then comes back where it was.
 */
export class NoticeStack {
  static readonly shared = new NoticeStack();

  private entries: Entry[] = [];
  private blocks = 0;
  private nextId = 1;
  private listening = false;
  private readonly onResize = (): void => this.flow();

  /** Put a notice `height` tall in the column; `place` hears where it goes
   *  (now, and whenever the column moves). `rect` reports its box. */
  add(height: number, place: (place: NoticePlace) => void, rect: (() => NoticeRect | null) | null = null): NoticeHandle {
    const entry: Entry = { id: this.nextId++, height, place, rect };
    this.entries.push(entry);
    this.listen();
    this.flow();
    let released = false;
    return {
      setHeight: (h) => {
        if (released || h === entry.height) return;
        entry.height = h;
        this.flow();
      },
      release: () => {
        if (released) return;
        released = true;
        this.entries = this.entries.filter((e) => e !== entry);
        this.flow();
      },
    };
  }

  /** A full-screen moment: every notice hides until the returned release. */
  block(): () => void {
    this.blocks += 1;
    this.flow();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.blocks = Math.max(0, this.blocks - 1);
      this.flow();
    };
  }

  /** The boxes of the notices showing (browser tests). */
  rects(): NoticeRect[] {
    if (this.blocks > 0) return [];
    return this.entries.map((e) => e.rect?.() ?? null).filter((r): r is NoticeRect => r !== null);
  }

  private flow(): void {
    const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const ys = noticeLayout(noticeTop(width, safeAreaInsetTop()), this.entries.map((e) => e.height));
    const shown = this.blocks === 0;
    this.entries.forEach((e, i) => e.place({ y: ys[i]!, shown }));
  }

  private listen(): void {
    if (this.listening || typeof window === 'undefined') return;
    this.listening = true;
    window.addEventListener('resize', this.onResize);
    window.__notices = { rects: () => this.rects() };
  }
}
